import React from 'react'
import { Link } from 'react-router-dom'
import Navigation from '@/components/Navigation'
import Footer from '@/components/Footer'
import {Coins, ShieldCheck, Download, CheckCircle, ArrowRight, TrendingUp, Globe, Zap} from 'lucide-react'

export default function CryptoPaymentPage() {
  const features = [
    {
      icon: Coins,
      title: 'Multiple Cryptocurrencies',
      description: 'Accept Bitcoin, Ethereum, USDT, USDC, Litecoin, and 50+ other cryptocurrencies automatically.'
    },
    {
      icon: Zap,
      title: 'Instant Confirmations',
      description: 'Real-time payment detection with automatic invoice marking and service activation.'
    },
    {
      icon: Globe,
      title: 'Global Payments',
      description: 'No borders, no restrictions. Accept payments from anywhere in the world 24/7.'
    },
    {
      icon: TrendingUp,
      title: 'Auto-Conversion',
      description: 'Optional automatic conversion to fiat currency to protect against volatility.'
    }
  ]

  const cryptoList = [
    'Bitcoin (BTC)',
    'Ethereum (ETH)',
    'Tether (USDT)',
    'USD Coin (USDC)',
    'Litecoin (LTC)',
    'Bitcoin Cash (BCH)',
    'Dogecoin (DOGE)',
    'And 50+ more coins'
  ]

  const benefits = [
    'Zero chargeback risk',
    'Lower transaction fees (1-2%)',
    'Instant global payments',
    'No bank intermediaries',
    'Privacy-focused transactions',
    'Automatic price conversion',
    'Built-in fraud protection',
    'Mobile-optimized checkout'
  ]

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-white py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <ShieldCheck className="w-5 h-5 text-green-500" />
                <span className="text-sm font-semibold text-green-700">Official Licensed Module</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
                WHMCS Crypto Payment Module
              </h1>
              
              <p className="text-xl text-slate-600 mb-8">
                Accept Bitcoin, USDT, and other cryptocurrencies automatically with this secure gateway. 
                Tap into the growing crypto economy with zero chargeback risk.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <button className="inline-flex items-center justify-center px-8 py-4 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-all hover:shadow-lg">
                  Buy Now - $149
                  <ArrowRight className="ml-2 w-5 h-5" />
                </button>
                <a
                  href="#features"
                  className="inline-flex items-center justify-center px-8 py-4 bg-white text-blue-600 font-semibold rounded-lg border-2 border-blue-600 hover:bg-blue-50 transition-all"
                >
                  View Features
                </a>
              </div>

              <div className="flex items-center space-x-2 text-sm text-slate-600">
                <Download className="w-4 h-4" />
                <span>Instant digital download • Lifetime updates included</span>
              </div>
            </div>

            <div className="bg-slate-900 rounded-xl p-8 border-2 border-slate-700">
              <div className="bg-slate-800 rounded-lg p-6 shadow-lg">
                <p className="text-slate-300 text-sm mb-4 font-mono">Crypto Payment Gateway</p>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-slate-700 rounded">
                    <div className="flex items-center space-x-2">
                      <div className="w-6 h-6 bg-orange-500 rounded-full"></div>
                      <span className="text-white text-sm font-medium">Bitcoin</span>
                    </div>
                    <span className="text-green-400 text-sm font-mono">✓ Enabled</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-700 rounded">
                    <div className="flex items-center space-x-2">
                      <div className="w-6 h-6 bg-blue-400 rounded-full"></div>
                      <span className="text-white text-sm font-medium">Ethereum</span>
                    </div>
                    <span className="text-green-400 text-sm font-mono">✓ Enabled</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-700 rounded">
                    <div className="flex items-center space-x-2">
                      <div className="w-6 h-6 bg-green-500 rounded-full"></div>
                      <span className="text-white text-sm font-medium">USDT</span>
                    </div>
                    <span className="text-green-400 text-sm font-mono">✓ Enabled</span>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-slate-600">
                  <p className="text-slate-400 text-xs">Total Transactions: 1,247</p>
                  <p className="text-slate-400 text-xs">Volume: $124,890</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Supported Cryptocurrencies */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Supported Cryptocurrencies
            </h2>
            <p className="text-lg text-slate-600">
              Accept all major cryptocurrencies and stablecoins
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-4">
            {cryptoList.map((crypto, index) => (
              <div key={index} className="flex items-center space-x-3 bg-slate-50 p-4 rounded-lg border border-gray-200">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                <span className="text-slate-700 font-medium">{crypto}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Modern Crypto Payment Processing
            </h2>
            <p className="text-lg text-slate-600">
              Built for the future of digital payments
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-slate-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Why Accept Cryptocurrency?
            </h2>
            <p className="text-lg text-slate-600">
              Expand your payment options and reach global customers
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start space-x-3">
                <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-slate-700">{benefit}</span>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-8 text-center">
            <h3 className="text-2xl font-bold text-white mb-4">
              Join the Crypto Payment Revolution
            </h3>
            <p className="text-blue-100 mb-6">
              Start accepting Bitcoin and other cryptocurrencies today
            </p>
            <button className="inline-flex items-center px-8 py-4 bg-white text-blue-600 font-semibold rounded-lg hover:bg-blue-50 transition-all hover:shadow-xl">
              Purchase Now - $149
              <ArrowRight className="ml-2 w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      {/* Security & Compliance */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <div className="bg-blue-50 border-l-4 border-blue-400 p-6 rounded-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <ShieldCheck className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-semibold text-blue-900 mb-2">
                  Secure & Non-Custodial
                </h3>
                <p className="text-blue-800">
                  This module uses industry-standard blockchain APIs with HD wallet support. 
                  Payments go directly to your wallet—we never hold your funds. All transactions 
                  are verified on-chain for maximum security and transparency.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white border-2 border-blue-600 rounded-2xl p-8 shadow-xl">
            <div className="text-center mb-8">
              <p className="text-sm font-semibold text-blue-600 mb-2">ONE-TIME PAYMENT</p>
              <p className="text-5xl font-bold text-slate-900 mb-2">$149</p>
              <p className="text-slate-600">Lifetime license • Free updates</p>
            </div>

            <div className="space-y-3 mb-8">
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className="text-slate-700">50+ cryptocurrencies supported</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className="text-slate-700">Real-time blockchain monitoring</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className="text-slate-700">Unlimited transactions</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className="text-slate-700">Premium technical support</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className="text-slate-700">30-day money-back guarantee</span>
              </div>
            </div>

            <button className="w-full px-8 py-4 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-all hover:shadow-lg">
              Add to Cart
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}
